import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Plus, Eye, UploadCloud, Trash2, Pencil, FileText, X, CheckCircle, RefreshCw } from 'lucide-react';
import { LegalDocumentService, LegalDocumentAdminDTO, LegalDocumentUpsert } from '../services/apiService';
import LegalTextEditor, { LegalTextEditorHandle } from '../components/LegalTextEditor';

// Gestione documenti legali: versioni per tipo, bozze, anteprima con i dati
// agenzia e pubblicazione. Pubblicare una nuova versione chiude la precedente
// e chiede di nuovo l'accettazione agli utenti al login successivo.

type DocType = LegalDocumentDTO_Type;
type LegalDocumentDTO_Type = LegalDocumentAdminDTO['type'];

const TYPE_LABELS: Record<DocType, string> = {
    PRIVACY_POLICY: 'Informativa privacy',
    TERMS_OF_SERVICE: 'Termini e condizioni',
    DPA_INSTRUCTIONS: 'Istruzioni al designato al trattamento',
    WORKER_MONITORING_NOTICE: 'Informativa controllo a distanza (GPS/foto)',
    CODICE_ETICO: 'Codice etico',
};
const TYPE_ORDER: DocType[] = [
    'PRIVACY_POLICY', 'TERMS_OF_SERVICE', 'DPA_INSTRUCTIONS', 'WORKER_MONITORING_NOTICE', 'CODICE_ETICO',
];

// La nuova versione parte dal testo dell'ultima pubblicata, con i segnaposto ancora da
// risolvere (template); i documenti seed hanno i segnaposto direttamente nel contenuto.
const draftFrom = (type: DocType, base?: LegalDocumentAdminDTO): LegalDocumentUpsert => ({
    type,
    title: base?.title || '',
    templateHtml: base ? (base.templateHtml?.trim() ? base.templateHtml : base.contentHtml || '') : '',
    changelogNote: '',
});

const fmtDate = (iso: string | null) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('it-IT'); } catch { return iso; }
};

export const LegalDocumentsAdminView: React.FC = () => {
    const [docs, setDocs] = useState<LegalDocumentAdminDTO[]>([]);
    const [placeholders, setPlaceholders] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    // Editor bozza (create o update).
    const [editing, setEditing] = useState<LegalDocumentUpsert | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState<string | null>(null);
    const [publishingId, setPublishingId] = useState<number | null>(null);
    const editorRef = useRef<LegalTextEditorHandle>(null);

    const load = async () => {
        setLoading(true);
        try {
            const [list, ph] = await Promise.all([
                LegalDocumentService.adminList(),
                LegalDocumentService.adminPlaceholders().catch(() => ({} as Record<string, string>)),
            ]);
            setDocs(list);
            setPlaceholders(ph);
            setError('');
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Errore nel caricamento dei documenti.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const grouped = useMemo(() => {
        const map: Record<string, LegalDocumentAdminDTO[]> = {};
        for (const d of docs) (map[d.type] ||= []).push(d);
        return map;
    }, [docs]);

    const placeholderKeys = Object.keys(placeholders);
    const hasDraftFor = (type: DocType) => docs.some(d => d.type === type && d.draft);

    // Ultima versione pubblicata (in vigore o, in mancanza, la più recente).
    const lastPublished = (type: DocType) => {
        const pub = (grouped[type] || []).filter(d => !d.draft);
        return pub.find(d => d.inForce) || pub[0];
    };

    const startCreate = (type: DocType) => {
        setEditingId(null);
        setEditing(draftFrom(type, lastPublished(type)));
        setPreview(null);
        setNotice('');
    };
    const startEdit = (d: LegalDocumentAdminDTO) => {
        setEditingId(d.id);
        setEditing({ type: d.type, title: d.title, templateHtml: d.templateHtml || '', changelogNote: d.changelogNote || '' });
        setPreview(null);
        setNotice('');
    };
    const closeEditor = () => { setEditing(null); setEditingId(null); setPreview(null); };

    const insertPlaceholder = (key: string) => {
        editorRef.current?.insertText(`{{${key}}}`);
    };

    const save = async () => {
        if (!editing) return;
        if (!editing.title.trim()) { setError('Il titolo è obbligatorio.'); return; }
        setSaving(true);
        setError('');
        try {
            if (editingId) await LegalDocumentService.adminUpdate(editingId, editing);
            else await LegalDocumentService.adminCreate(editing);
            setNotice('Bozza salvata.');
            closeEditor();
            await load();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Errore nel salvataggio della bozza.');
        } finally {
            setSaving(false);
        }
    };

    const doPreview = async () => {
        if (!editing) return;
        setError('');
        try {
            const html = await LegalDocumentService.adminPreview(editing);
            setPreview(html);
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Impossibile generare l\'anteprima (verifica i dati agenzia in Impostazioni).');
        }
    };

    const doDelete = async (id: number) => {
        if (!window.confirm('Eliminare questa bozza? L\'azione non è reversibile.')) return;
        setError('');
        try {
            await LegalDocumentService.adminDelete(id);
            await load();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Errore nell\'eliminazione.');
        }
    };

    const doPublish = async (d: LegalDocumentAdminDTO) => {
        if (!window.confirm(
            `Pubblicare "${d.title}" v${d.version}?\n\n` +
            'Il testo verrà congelato con i dati agenzia correnti e reso immutabile. ' +
            'La versione precedente sarà sostituita e gli utenti dovranno ri-accettare al prossimo accesso.'
        )) return;
        setPublishingId(d.id);
        setError('');
        try {
            await LegalDocumentService.adminPublish(d.id);
            setNotice(`Documento "${d.title}" v${d.version} pubblicato.`);
            await load();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Errore nella pubblicazione.');
        } finally {
            setPublishingId(null);
        }
    };

    const doRepublish = async (d: LegalDocumentAdminDTO) => {
        if (!window.confirm(
            `Ripubblicare "${d.title}" risolvendo i segnaposto con i dati di Impostazioni?\n\n` +
            'Verrà creata una nuova versione con nome agenzia, P.IVA, PEC, licenza, ecc. ' +
            'al posto dei {{...}}. Assicurati di aver compilato i dati in Impostazioni.'
        )) return;
        setPublishingId(d.id);
        setError('');
        try {
            await LegalDocumentService.adminRepublish(d.id);
            setNotice(`Documento "${d.title}" ripubblicato con i dati agenzia.`);
            await load();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Errore nella ripubblicazione (verifica i dati agenzia in Impostazioni).');
        } finally {
            setPublishingId(null);
        }
    };

    const statusBadge = (d: LegalDocumentAdminDTO) => {
        if (d.draft) return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">Bozza</span>;
        if (d.inForce) return <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">In vigore</span>;
        return <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-semibold">Superata da v{d.supersededByVersion}</span>;
    };

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Documenti legali</h1>
                <p className="text-sm text-gray-500 mt-1">
                    Gestisci le versioni dei documenti che gli utenti interni devono accettare.
                    Usa i segnaposto (es. <code className="bg-gray-100 px-1 rounded">{'{{NOME_TITOLARE}}'}</code>):
                    alla pubblicazione vengono sostituiti con i dati dell'agenzia da Impostazioni.
                </p>
            </div>

            {error && (
                <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</div>
            )}
            {notice && (
                <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                    <CheckCircle size={16} /> {notice}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-indigo-600" size={32} /></div>
            ) : (
                <div className="space-y-5">
                    {TYPE_ORDER.map(type => {
                        const versions = grouped[type] || [];
                        return (
                            <div key={type} className="bg-white rounded-xl border border-gray-200 shadow-sm">
                                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                                    <div className="flex items-center gap-2">
                                        <FileText size={18} className="text-indigo-600" />
                                        <h2 className="font-bold text-gray-900">{TYPE_LABELS[type]}</h2>
                                    </div>
                                    <button
                                        onClick={() => startCreate(type)}
                                        disabled={hasDraftFor(type)}
                                        title={hasDraftFor(type) ? 'Esiste già una bozza: modificala o pubblicala' : 'Nuova versione'}
                                        className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                                    >
                                        <Plus size={16} /> {lastPublished(type) ? `Nuova versione dalla v${lastPublished(type)!.version}` : 'Nuova versione'}
                                    </button>
                                </div>
                                {versions.length === 0 ? (
                                    <div className="p-4 text-sm text-gray-400">Nessuna versione. Crea la prima bozza.</div>
                                ) : (
                                    <ul className="divide-y divide-gray-100">
                                        {versions.map(d => (
                                            <li key={d.id} className="p-4 flex items-center justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-semibold text-gray-800">v{d.version}</span>
                                                        {statusBadge(d)}
                                                        <span className="text-sm text-gray-600 truncate">{d.title}</span>
                                                    </div>
                                                    <div className="text-xs text-gray-400 mt-1">
                                                        {d.draft
                                                            ? 'Non ancora pubblicata'
                                                            : `Pubblicata ${fmtDate(d.publishedAt)} · ${d.signaturesCount} firme`}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    {d.draft ? (
                                                        <>
                                                            <button onClick={() => startEdit(d)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" title="Modifica"><Pencil size={16} /></button>
                                                            <button onClick={() => doDelete(d.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Elimina"><Trash2 size={16} /></button>
                                                            <button
                                                                onClick={() => doPublish(d)}
                                                                disabled={publishingId === d.id}
                                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:bg-gray-300"
                                                            >
                                                                {publishingId === d.id ? <Loader2 className="animate-spin" size={14} /> : <UploadCloud size={14} />}
                                                                Pubblica
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button onClick={() => setPreview(d.contentHtml || '')} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" title="Vedi testo pubblicato"><Eye size={16} /></button>
                                                            {d.inForce && (
                                                                <button
                                                                    onClick={() => doRepublish(d)}
                                                                    disabled={publishingId === d.id}
                                                                    title="Ripubblica risolvendo i segnaposto con i dati di Impostazioni"
                                                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-700 text-sm font-semibold hover:bg-indigo-50 disabled:opacity-50"
                                                                >
                                                                    {publishingId === d.id ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
                                                                    Ripubblica coi dati agenzia
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Editor bozza */}
            {editing && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch sm:items-center justify-center p-0 sm:p-6">
                    <div className="bg-white w-full sm:max-w-4xl sm:rounded-2xl shadow-2xl flex flex-col max-h-screen sm:max-h-[94vh]">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <h2 className="text-lg font-bold text-gray-900">
                                {editingId ? 'Modifica bozza' : 'Nuova versione'} · {TYPE_LABELS[editing.type]}
                            </h2>
                            <button onClick={closeEditor} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><X size={20} /></button>
                        </div>
                        <div className="p-5 overflow-y-auto flex-1 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Titolo</label>
                                <input
                                    type="text"
                                    value={editing.title}
                                    onChange={e => setEditing({ ...editing, title: e.target.value })}
                                    placeholder="es. Informativa privacy"
                                    className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            {placeholderKeys.length > 0 && (
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Segnaposto agenzia (clic per inserirlo dove c'è il cursore)</label>
                                    <div className="flex flex-wrap gap-2">
                                        {placeholderKeys.map(k => {
                                            const vuoto = !placeholders[k];
                                            return (
                                                <button
                                                    key={k}
                                                    type="button"
                                                    onClick={() => insertPlaceholder(k)}
                                                    title={vuoto ? 'Dato non compilato in Impostazioni: la pubblicazione verrà bloccata' : `Diventerà: ${placeholders[k]}`}
                                                    className={`text-xs px-2 py-1 rounded-md font-mono ${vuoto
                                                        ? 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
                                                        : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}
                                                >
                                                    {`{{${k}}}`}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="text-[11px] text-gray-400 mt-1">
                                        Passa col mouse su un segnaposto per vedere con cosa verrà sostituito.
                                        In arancione quelli senza dato in Impostazioni{placeholders.EMAIL_DPO === '' ? ' (EMAIL_DPO vuota è ammessa)' : ''}.
                                    </p>
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Testo</label>
                                <LegalTextEditor
                                    ref={editorRef}
                                    value={editing.templateHtml}
                                    onChange={html => setEditing(prev => prev ? { ...prev, templateHtml: html } : prev)}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Cosa è cambiato (mostrato in ri-accettazione)</label>
                                <input
                                    type="text"
                                    value={editing.changelogNote || ''}
                                    onChange={e => setEditing({ ...editing, changelogNote: e.target.value })}
                                    placeholder="es. Aggiornato il paragrafo sul trattamento GPS"
                                    className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            {preview !== null && (
                                <div className="border border-gray-200 rounded-lg">
                                    <div className="px-3 py-2 border-b border-gray-100 text-xs font-bold text-gray-500 uppercase">Anteprima (dati agenzia risolti)</div>
                                    <div
                                        className="p-4 text-sm text-gray-800 max-h-64 overflow-y-auto
                                            [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-3
                                            [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2
                                            [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mb-1"
                                        dangerouslySetInnerHTML={{ __html: preview }}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="p-4 border-t border-gray-200 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                            <button onClick={doPreview} className="inline-flex items-center justify-center gap-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50">
                                <Eye size={16} /> Anteprima
                            </button>
                            <button
                                onClick={save}
                                disabled={saving}
                                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 disabled:bg-gray-300"
                            >
                                {saving && <Loader2 className="animate-spin" size={16} />}
                                Salva bozza
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Anteprima testo pubblicato (sola lettura) */}
            {preview !== null && !editing && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch sm:items-center justify-center p-0 sm:p-6" onClick={() => setPreview(null)}>
                    <div className="bg-white w-full sm:max-w-3xl sm:rounded-2xl shadow-2xl flex flex-col max-h-screen sm:max-h-[90vh]" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <h2 className="text-lg font-bold text-gray-900">Testo pubblicato</h2>
                            <button onClick={() => setPreview(null)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><X size={20} /></button>
                        </div>
                        <div
                            className="p-6 overflow-y-auto flex-1 text-sm text-gray-800
                                [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4
                                [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-2
                                [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mb-1"
                            dangerouslySetInnerHTML={{ __html: preview }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default LegalDocumentsAdminView;
